const yup = require("yup");
const { generateCustomFieldsValidation } = require("./customFieldValidations");
const { allocationCodeRequiredIfType } = require("./allocation");
const {
  DEFAULT_STRIPE_EXPIRATION_MONTHS,
} = require("utils/cards/defaultStripeExpirationDateHelper");

const createValidationSchema = ({
  transactionLimitField,
  dailyLimitField,
  weeklyLimitField,
  monthlyLimitField,
  totalLimitField,
  allocationTypeField,
  allocationCodeField,
  merchantField,
  receiptPolicyField,
  allocationPolicyField,
  additionalFields,
  cancelationTimeFrameField,
  cancelationDateField,
}) => {
  let schema = {
    [transactionLimitField]: yup
      .number()
      .positive("Must be positive")
      .integer("No decimals allowed")
      .max(10000, "Must be 10k or less")
      .nullable(true),
    [dailyLimitField]: yup
      .number()
      .positive("Must be positive")
      .integer("No decimals allowed")
      .nullable(true),
    [weeklyLimitField]: yup
      .number()
      .positive("Must be positive")
      .integer("No decimals allowed")
      .nullable(true),
    [monthlyLimitField]: yup
      .number()
      .positive("Must be positive")
      .integer("No decimals allowed")
      .nullable(true),
    [totalLimitField]: yup
      .number()
      .positive("Must be positive")
      .integer("No decimals allowed")
      .nullable(true),

    [allocationTypeField]: yup
      .object()
      .shape({
        key: yup.string().optional().nullable(true),
      })
      .nullable(true),

    [allocationCodeField]: yup
      .object()
      .shape({
        id: yup.string(),
        allocationCode: yup.string(),
      })
      .test(
        "allocation-required",
        "Allocation Name is required when an Allocation Type is selected",
        allocationCodeRequiredIfType(),
      ),

    [cancelationTimeFrameField]: yup
      .object()
      .shape({
        timeFrameValue: yup
          .number()
          .transform((value, originalValue) => (isNaN(value) ? null : value))
          .positive("Must be in the future")
          .integer("No decimals allowed")
          .optional()
          .nullable(true),
        timeFrameFrequency: yup.string().optional().nullable(true),
      })
      .test(
        "cancelationTimeFrame-both-or-none",
        "Value and frequency must be set",
        function (value) {
          // value is the cancelationTimeFrame object
          const hasValue = value?.timeFrameValue && value.timeFrameValue !== "";
          const hasFrequency =
            value?.timeFrameFrequency && value.timeFrameFrequency !== "";
          // Only valid if both are set or both are empty
          return (hasValue && hasFrequency) || (!hasValue && !hasFrequency);
        },
      )
      .test(
        "cancelationTimeFrame-max-months",
        `Cannot be longer than ${DEFAULT_STRIPE_EXPIRATION_MONTHS} months`,
        function (value) {
          const { timeFrameValue, timeFrameFrequency } = value || {};
          if (!timeFrameValue || !timeFrameFrequency) return true; // skip if not set

          let months;
          if (timeFrameFrequency === "month") {
            months = timeFrameValue;
          } else if (timeFrameFrequency === "week") {
            months = timeFrameValue / 4.345; // approx weeks per month
          } else if (timeFrameFrequency === "day") {
            months = timeFrameValue / 30.44; // approx days per month
          } else {
            return true;
          }
          return months <= DEFAULT_STRIPE_EXPIRATION_MONTHS;
        },
      ),

    [cancelationDateField]: yup
      .date()
      .transform((value, originalValue) => {
        if (typeof originalValue === "number") {
          return new Date(originalValue);
        }
        if (
          typeof originalValue === "string" &&
          !isNaN(Number(originalValue))
        ) {
          return new Date(Number(originalValue));
        }
        return value;
      })
      .nullable(true)
      .test(
        "cancelationDate-valid",
        "Cancelation date must be before expiration date",
        function (value) {
          const expirationDate = this?.parent.expirationDate;
          if (!value || !expirationDate) return true;
          return new Date(value) < new Date(expirationDate);
        },
      ),

    [receiptPolicyField]: yup.string().required("Receipt Policy is required"),
    [allocationPolicyField]: yup
      .string()
      .required("Allocation Policy is required"),

    [merchantField]: yup.array().of(yup.string()).optional(),
  };

  schema = { ...schema, ...additionalFields };
  let validationSchema = yup.object().shape(schema);

  return validationSchema;
};

const presetLimits = createValidationSchema({
  transactionLimitField: "transactionLimit",
  dailyLimitField: "dailyLimit",
  weeklyLimitField: "weeklyLimit",
  monthlyLimitField: "monthlyLimit",
  totalLimitField: "totalLimit",
  cancelationTimeFrameField: "cancelationTimeFrame",
  cancelationDateField: "cancelationDate",
  merchantField: "merchantLimit",
  allocationTypeField: "allocationType",
  allocationCodeField: "allocationCode",
  allocationPolicyField: "allocationPolicy",
  receiptPolicyField: "receiptPolicy",
  additionalFields: {
    nameOfPreset: yup.string().required("Name of Preset is required"),
  },
});

const generateCardValidationSchema = (customFieldSettings) => {
  const baseSchema = createValidationSchema({
    transactionLimitField: "transactionLimit",
    dailyLimitField: "dailyLimit",
    weeklyLimitField: "weeklyLimit",
    monthlyLimitField: "monthlyLimit",
    totalLimitField: "totalLimit",
    cancelationTimeFrameField: "cancelationTimeFrame",
    cancelationDateField: "cancelationDate",
    merchantField: "merchantLimit",
    allocationTypeField: "allocationType",
    allocationCodeField: "allocationCode",
    allocationPolicyField: "allocationPolicy",
    receiptPolicyField: "receiptPolicy",
  });

  // Add dynamic custom fields validation
  const customFieldsSchema =
    generateCustomFieldsValidation(customFieldSettings);

  return yup.object().shape({
    ...baseSchema.fields, // Include the base schema fields
    customFields: customFieldsSchema, // Add custom fields validation
  });
};

export { presetLimits, generateCardValidationSchema };
